import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { format, addMonths } from 'date-fns';
import { Building2, Plus, Minus, Slash, ArrowRight } from 'lucide-react';
import { Modal } from '../../../../components/common/Modal';
import { SegmentedToggle } from '../../../../components/common/SegmentedToggle';
import { useLanguage } from '../../../../features/language/useLanguage';
import {
  subscriptionService,
  type AdjustInput,
  type CompanySubscriptionRow,
  type SubscriptionTier,
} from '../../../../services/subscriptionService';
import { TierBadge } from './TierBadge';

type AdjustmentMode = 'none' | 'add' | 'reduce';

/** Admin modal to grant/reduce subscription months and/or change a company's tier, with a confirm stage. */
export function AdjustSubscriptionModal({ row, onClose }: { row: CompanySubscriptionRow; onClose: () => void }) {
  const { t } = useLanguage();
  const qc = useQueryClient();
  const [mode, setMode] = useState<AdjustmentMode>('add');
  const [months, setMonths] = useState(1);
  const [tier, setTier] = useState<SubscriptionTier | ''>('');
  const [reason, setReason] = useState('');
  const [stage, setStage] = useState<'edit' | 'confirm'>('edit');

  const adjust = useMutation({
    mutationFn: (input: AdjustInput) => subscriptionService.adminAdjust(row.company_id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'companies-subscriptions'] });
      onClose();
    },
  });

  const monthsDelta = mode === 'add' ? Math.max(0, months) : mode === 'reduce' ? -Math.max(0, months) : 0;
  const tierChanged = tier !== '' && tier !== row.tier;
  const monthsChanged = mode !== 'none' && months > 0;
  const reasonValid = reason.trim().length > 0;
  const monthsModeValid = mode === 'none' || months > 0;
  const hasAnyChange = tierChanged || monthsChanged;
  const isValid = reasonValid && monthsModeValid && hasAnyChange;

  const modeItems: { key: AdjustmentMode; label: string; icon: React.ReactNode }[] = [
    { key: 'none', label: t('subscription.admin.field.adjustment_none'), icon: <Slash className="h-3.5 w-3.5" /> },
    { key: 'add', label: t('subscription.admin.field.adjustment_add'), icon: <Plus className="h-3.5 w-3.5" /> },
    { key: 'reduce', label: t('subscription.admin.field.adjustment_reduce'), icon: <Minus className="h-3.5 w-3.5" /> },
  ];

  if (stage === 'confirm') {
    const currentExpires = row.expires_at ? new Date(row.expires_at) : null;
    const baseForNewExpiry = currentExpires && currentExpires > new Date() ? currentExpires : new Date();
    const newExpires = monthsDelta !== 0 ? addMonths(baseForNewExpiry, monthsDelta) : currentExpires;
    const effectiveNewTier: SubscriptionTier = tier === '' ? row.tier : tier;

    return (
      <Modal isOpen onClose={onClose} title={t('subscription.admin.confirm.title')} maxWidth="md">
        <div className="space-y-4">
          <p className="text-sm text-gray-600 -mt-1">{t('subscription.admin.confirm.intro')}</p>

          <div className="rounded-lg border border-gray-200 bg-gray-50/60 divide-y divide-gray-200">
            <SummaryRow
              label={t('subscription.admin.confirm.company')}
              value={
                <span className="inline-flex items-center gap-2 text-gray-900 font-medium">
                  <Building2 className="h-4 w-4 text-gray-400" />
                  {row.company_name}
                </span>
              }
            />
            <SummaryRow
              label={t('subscription.admin.confirm.new_tier')}
              value={
                tierChanged ? (
                  <span className="inline-flex items-center gap-2">
                    <TierBadge tier={row.tier} />
                    <ArrowRight className="h-3.5 w-3.5 text-gray-400" />
                    <TierBadge tier={effectiveNewTier} />
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-2">
                    <TierBadge tier={row.tier} />
                    <span className="text-xs text-gray-500">({t('subscription.admin.confirm.tier_unchanged')})</span>
                  </span>
                )
              }
            />
            <SummaryRow label={t('subscription.admin.confirm.months_change')} value={<MonthsChangeBadge mode={mode} months={months} t={t} />} />
            <SummaryRow
              label={t('subscription.admin.confirm.expires_current')}
              value={<span className="text-gray-700 tabular-nums">{currentExpires ? format(currentExpires, 'PPP') : '—'}</span>}
            />
            {monthsDelta !== 0 && (
              <SummaryRow
                label={t('subscription.admin.confirm.expires_new')}
                value={<span className="text-gray-900 font-medium tabular-nums">{newExpires ? format(newExpires, 'PPP') : '—'}</span>}
              />
            )}
            <SummaryRow
              label={t('subscription.admin.confirm.reason')}
              value={<span className="text-gray-700 whitespace-pre-wrap break-words">{reason.trim()}</span>}
              alignTop
            />
          </div>

          {adjust.isError && (
            <p className="text-sm text-red-600">{(adjust.error as Error)?.message || t('common.error')}</p>
          )}

          <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
            <button
              onClick={() => setStage('edit')}
              disabled={adjust.isPending}
              className="px-4 py-2 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors"
              data-test-id="admin-adjust-back"
            >
              {t('subscription.admin.confirm.back')}
            </button>
            <button
              disabled={!isValid || adjust.isPending}
              onClick={() =>
                adjust.mutate({
                  months_delta: monthsDelta,
                  tier: tier || undefined,
                  reason: reason.trim(),
                })
              }
              className="px-4 py-2 rounded-md bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-test-id="admin-adjust-confirm"
            >
              {adjust.isPending ? t('common.loading') : t('subscription.admin.confirm.confirm_cta')}
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen onClose={onClose} title={t('subscription.admin.adjust_title')} maxWidth="md">
      <div className="space-y-4">
        <div className="flex items-center gap-2 -mt-1 text-xs">
          <Building2 className="h-3.5 w-3.5 text-gray-400" />
          <span className="text-gray-500">{row.company_name}</span>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1.5">
            {t('subscription.admin.field.adjustment_type')}
          </label>
          <SegmentedToggle
            ariaLabel={t('subscription.admin.field.adjustment_type')}
            value={mode}
            onChange={setMode}
            fullWidth
            minTabWidth="0"
            testIdPrefix="admin-adjust-mode"
            items={modeItems}
          />
        </div>

        {mode !== 'none' && (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              {t('subscription.admin.field.months_amount')}
            </label>
            <input
              type="number"
              min={1}
              value={months}
              onChange={(e) => {
                const raw = parseInt(e.target.value || '0', 10);
                setMonths(Number.isFinite(raw) ? Math.max(0, raw) : 0);
              }}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              data-test-id="admin-adjust-months"
            />
            {months <= 0 && (
              <p className="mt-1 text-xs text-amber-600">{t('subscription.admin.validation.months_positive')}</p>
            )}
          </div>
        )}

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1.5">{t('subscription.admin.col.tier')}</label>
          <select
            value={tier}
            onChange={(e) => setTier(e.target.value as SubscriptionTier | '')}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            data-test-id="admin-adjust-tier"
          >
            <option value="">{t('subscription.admin.field.tier_unchanged')}</option>
            <option value="free">{t('subscription.tier.free')}</option>
            <option value="basic">{t('subscription.tier.basic')}</option>
            <option value="plus">{t('subscription.tier.plus')}</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1.5">{t('subscription.admin.field.reason')}</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('subscription.admin.field.reason')}
            rows={3}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
            data-test-id="admin-adjust-reason"
          />
        </div>

        {!hasAnyChange && reasonValid && monthsModeValid && (
          <p className="text-xs text-amber-600">{t('subscription.admin.validation.nothing_changed')}</p>
        )}

        <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={adjust.isPending}
            className="px-4 py-2 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            disabled={!isValid || adjust.isPending}
            onClick={() => setStage('confirm')}
            className="px-4 py-2 rounded-md bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-test-id="admin-adjust-submit"
          >
            {t('common.save')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function SummaryRow({
  label,
  value,
  alignTop = false,
}: {
  label: string;
  value: React.ReactNode;
  alignTop?: boolean;
}) {
  return (
    <div className={`flex gap-4 px-3 py-2.5 ${alignTop ? 'items-start' : 'items-center'}`}>
      <div className="w-32 shrink-0 text-xs font-medium uppercase tracking-wider text-gray-500">{label}</div>
      <div className="flex-1 text-sm">{value}</div>
    </div>
  );
}

function MonthsChangeBadge({
  mode,
  months,
  t,
}: {
  mode: AdjustmentMode;
  months: number;
  t: (key: string, params?: Record<string, string | number>) => string;
}) {
  if (mode === 'none' || months <= 0) {
    return <span className="text-gray-500">{t('subscription.admin.confirm.months_none')}</span>;
  }
  if (mode === 'add') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-md bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200 px-2 py-0.5 text-xs font-semibold">
        <Plus className="h-3 w-3" />
        {t('subscription.admin.confirm.months_extend', { count: months })}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-200 px-2 py-0.5 text-xs font-semibold">
      <Minus className="h-3 w-3" />
      {t('subscription.admin.confirm.months_shorten', { count: months })}
    </span>
  );
}
